<script setup lang="ts">
import { DisneylandCampaignModal } from '@apps/main/new-desktop/modules/disneylandCampaign';
import {
  useBookTripStore,
  useBrowserStore,
  useCoreStore,
  usePaxStore,
  useStatsigEventsStore,
  useDisneylandCampaignStore,
} from '@apps/main/new-desktop/core/stores';
import { useDestinationSelectorStore } from '@apps/main/new-desktop/core/stores/destinationSelectorStore';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import { TransportType } from '@libs/trip-planner-interfaces';

import { PriceBreakdownModal } from '../../../modules/priceBreakdownModal';
import type { EditTripSegment } from '../../../modules/editTripModal';
import { EditTripModal } from '../../../modules/editTripModal';
import { DestinationSelectorModal } from '../../../modules/destinationSelector';
import { FerryMultipleVehiclesModal } from '../../../modules/ferryMultipleVehiclesModal';
import { WhyWeSuggestModal } from '../../../modules/suggestionTypeSelector';
import { PriceGuaranteeModal } from '../../../modules/priceGuarantee';
import { RemoveDestinationModal } from '../../../modules/removeDestinationModal';
import { AddFlightsModalNew } from '../../../modules/addFlightsModal';
import type { AddFligthsModalRoute } from '../../../modules/addFlightsModal';
import { FerryDetailsModal } from '../../../modules/ferryDetailsModal';
import { FlightDetailsModal } from '../../../modules/flightDetailsModal';
import { ExternalErrorModal } from '../../../modules/externalErrorModal';
import type { PortSelectionUpdatePayload } from '../models';
import { TripOverviewModalType } from '../models';
import {
  usePremadeTripStore,
  useTripOverviewPageStore,
  useTripOverviewModalsStore,
  usePortSelectorStore,
  useTripOverviewFerryMultipleVehiclesStore,
} from '../stores';

import ContinueWithSelectedDateModal from './modals/ContinueWithSelectedDateModal.vue';
import PremadeTripDescriptionModal from './modals/PremadeTripDescriptionModal.vue';
import { PortSelectorModal } from './modals/portSelectorModal';
import BookingErrorModal from './BookingErrorModal.vue';

const bookTripStore = useBookTripStore();
const browserStore = useBrowserStore();

const coreStore = useCoreStore();
const destinationSelectorStore = useDestinationSelectorStore();
const editTripStore = useEditTripStore();

const mapStore = useMapStore();
const paxStore = usePaxStore();
const portSelectorStore = usePortSelectorStore();
const premadeTripStore = usePremadeTripStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewFerryMultipleVehiclesStore =
  useTripOverviewFerryMultipleVehiclesStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const disneylandCampaignStore = useDisneylandCampaignStore();

function onConfirmAddFlightsModal(routes: AddFligthsModalRoute[]): void {
  statsigEventsStore.logTripOverviewFlightAdded();
  tripOverviewPageStore.confirmAddFlightsModal(routes);
}

function onConfirmPortSelectionModal(
  payload: PortSelectionUpdatePayload
): void {
  tripOverviewPageStore.setUserSelectedTransport(true);
  if (portSelectorStore.transportType === TransportType.Flight) {
    statsigEventsStore.logTripOverviewFlightAdded();
  }
  if (portSelectorStore.transportType === TransportType.Ferry) {
    statsigEventsStore.logTripOverviewFerryAdded();
  }
  tripOverviewModalsStore.confirmPortSelectionModal(payload);
}

function onBuildANewTrip(): void {
  statsigEventsStore.logBookingFailedErrorBuildNewTrip();
  tripOverviewModalsStore.buildNewTrip();
}

function onTryAgain(): void {
  statsigEventsStore.logBookingFailedErrorTryAgain();
  tripOverviewModalsStore.closeTripOverviewModal();
}

function handleChangeNumberOfNights(segment: EditTripSegment): void {
  editTripStore.changeNumberOfNights(
    segment.ingredientId,
    segment.numberOfNights
  );
}
</script>

<template>
  <AddFlightsModalNew
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.AddFlights
    "
    :routes="tripOverviewPageStore.getAddFlightsModalRoutes()"
    :start-positon="mapStore.startingLocation"
    :end-position="mapStore.endLocation"
    @close="tripOverviewModalsStore.closeAddFlightsModal()"
    @confirm="onConfirmAddFlightsModal"
  />

  <BookingErrorModal v-if="bookTripStore.bookingError" />

  <DestinationSelectorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.DestinationSelector
    "
    :search-text="destinationSelectorStore.searchText"
    :autocomplete-type="destinationSelectorStore.autocompleteType"
    @close-modal="tripOverviewModalsStore.closeTripOverviewModal"
    @select-my-position="tripOverviewModalsStore.selectUserPosition"
    @change-destination="
      tripOverviewModalsStore.confirmDestinationSelectorModal
    "
  />

  <FerryDetailsModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.FerryDetails &&
      tripOverviewModalsStore.ferryDetailsModalData
    "
    :ferry-data="tripOverviewModalsStore.ferryDetailsModalData"
    :image-handler="coreStore.handleImageLoadError"
    :pax="paxStore.getPaxesButtonSimpleText"
    @close="tripOverviewModalsStore.closeFerryDetailsModal()"
  />
  <FlightDetailsModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.FlightDetails &&
      tripOverviewModalsStore.flightDetailsModalData !== undefined
    "
    :flight-data="tripOverviewModalsStore.flightDetailsModalData"
    :image-handler="coreStore.handleImageLoadError"
    :pax="paxStore.getPaxesButtonSimpleText"
    @close="tripOverviewModalsStore.closeFlightDetailsModal"
  />

  <PortSelectorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.PortSelector
    "
    :transport-type="portSelectorStore.transportType"
    :initial-from-port="portSelectorStore.initalFromPort"
    :initial-to-port="portSelectorStore.initalToPort"
    :ferry-suggestions="portSelectorStore.ferryRoutesSuggestions"
    :can-add-return-ingredient="portSelectorStore.canAddReturnIngredient"
    @close="tripOverviewPageStore.closePortSelectionModal"
    @confirm="onConfirmPortSelectionModal"
  />

  <FerryMultipleVehiclesModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.FerryMultipleVehicles &&
      tripOverviewFerryMultipleVehiclesStore.data
    "
    :data="tripOverviewFerryMultipleVehiclesStore.data"
    @confirm="tripOverviewModalsStore.confirmFerryMultipleVehiclesModal"
    @cancel="tripOverviewModalsStore.closeFerryMultipleVehiclesModal"
  />

  <PriceGuaranteeModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.PriceGuarantee
    "
    @close="tripOverviewModalsStore.closeTripOverviewModal"
  />

  <PremadeTripDescriptionModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.PremadeTripDescription &&
      premadeTripStore.premadeTrip
    "
    :premade-trip="premadeTripStore.premadeTrip"
    @close="tripOverviewModalsStore.closeTripOverviewModal"
  />

  <RemoveDestinationModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.RemoveDestination
    "
    @close="tripOverviewModalsStore.closeTripOverviewModal"
    @confirm="tripOverviewModalsStore.confirmRemoveDestinaionModal"
  />

  <WhyWeSuggestModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.WhyWeSuggest
    "
    @close="tripOverviewModalsStore.closeTripOverviewModal"
  />

  <EditTripModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.EditTrip
    "
    :is-desktop="!browserStore.isSmallerThan600px"
    :segments="editTripStore.modalData"
    :initial-step="editTripStore.initialStep"
    @close="tripOverviewModalsStore.closeEditTripModal"
    @change-trip-order="editTripStore.changeTripOrder"
    @add-destination="destinationSelectorStore.handleConfirmSelection"
    @add-destination-to-own-stay="
      destinationSelectorStore.handleOwnStayConfirmSelection(
        $event.suggestion,
        $event.ingredientId
      )
    "
    @change-destination="
      destinationSelectorStore.changeDestinationByIngredientId(
        $event.suggestion,
        $event.ingredientId
      )
    "
    @change-number-of-nights="handleChangeNumberOfNights"
    @remove-destination="destinationSelectorStore.removeDestination"
  />

  <ContinueWithSelectedDateModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.ContinueWithSuggestedDate
    "
    :trip-date-range="tripOverviewModalsStore.tripDateRange"
    :trip-number-of-nights="tripOverviewModalsStore.tripNumberOfNights"
    @confrim="tripOverviewModalsStore.confirmContinueWithSuggestedDateModal"
    @close="tripOverviewModalsStore.closeContinueWithSuggestedDateModal"
  />

  <PriceBreakdownModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.PriceBreakdown &&
      tripOverviewModalsStore.stayPriceBreakdown
    "
    :data="tripOverviewModalsStore.stayPriceBreakdown"
    @close="tripOverviewModalsStore.closePriceBreakdownModal"
  />

  <ExternalErrorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.ExternalError
    "
    @build-a-new-trip="onBuildANewTrip"
    @try-again="onTryAgain"
  />

  <DisneylandCampaignModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.DisneylandCampaign &&
      disneylandCampaignStore.modalData
    "
    :modal-data="disneylandCampaignStore.modalData"
    @close="tripOverviewModalsStore.closeDisneylandCampaignModal"
  />
</template>

<style scoped lang="scss">
.filters-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-16);

  .reset-filters-button {
    text-align: left;
  }
}
</style>
